"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { membershipPlan, pricingLicenseNote, pricingPaymentNote } from "@/data/pricing";

export function PricingPage() {
  const reduceMotion = useReducedMotion();

  return (
    <main className="min-h-screen bg-background pb-16 pt-32 sm:pb-24">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12">
        <header className="mx-auto max-w-4xl text-center sm:mx-0 sm:text-left">
          <h1 className="font-display text-[clamp(4rem,10vw,8.5rem)] font-black uppercase leading-[.84]">
            Un tarif,
            <br />
            <span className="text-primary">pour tous.</span>
          </h1>
          <p className="mx-auto mt-7 max-w-xl text-sm font-medium leading-7 text-muted-foreground sm:mx-0 sm:text-base sm:leading-8">
            Un tarif unique pour les enfants et les adultes. Le premier cours est offert,
            quel que soit ton âge ou ton niveau.
          </p>
          <div className="mx-auto mt-8 grid max-w-3xl gap-3 text-left sm:mx-0 sm:grid-cols-2">
            <div className="flex items-center justify-center gap-3 border border-primary/35 bg-primary/8 px-4 py-3.5 sm:justify-start">
              <span className="grid size-9 shrink-0 place-items-center bg-primary text-white"><ShieldCheck className="size-4" /></span>
              <div><p className="text-[.62rem] font-extrabold uppercase tracking-[.13em] text-primary">Inclus dans l’adhésion</p><p className="mt-1 text-xs font-semibold text-foreground">{pricingLicenseNote}</p></div>
            </div>
            <div className="flex items-center justify-center gap-3 border border-border bg-card px-4 py-3.5 sm:justify-start">
              <span className="grid size-9 shrink-0 place-items-center bg-muted text-primary"><Check className="size-4" /></span>
              <div><p className="text-[.62rem] font-extrabold uppercase tracking-[.13em] text-muted-foreground">Pour commencer</p><p className="mt-1 text-xs font-semibold text-foreground">Premier cours offert</p></div>
            </div>
          </div>
        </header>

        <motion.article
          animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          initial={reduceMotion ? false : { opacity: 0, y: 18 }}
          transition={{ duration: 0.5 }}
          className="relative mt-12 max-w-3xl overflow-hidden border border-primary bg-card p-6 shadow-[0_18px_55px_rgba(181,42,61,.14)] sm:p-9"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
          <p className="text-xs font-black uppercase tracking-[.14em] text-primary">Tarif unique</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase sm:text-5xl">{membershipPlan.title}</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{membershipPlan.description}</p>
          <div className="mt-7 border-y border-border py-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <span className="text-sm font-semibold">{membershipPlan.audience}</span>
              <span className="font-display text-6xl font-black leading-none">{membershipPlan.amount}<sup className="text-3xl">€</sup><span className="ml-2 font-sans text-sm font-semibold text-muted-foreground">/ an</span></span>
            </div>
            <p className="mt-4 text-sm font-bold text-primary">{pricingPaymentNote}</p>
            <p className="mt-2 text-xs text-muted-foreground">{pricingLicenseNote}</p>
          </div>
          <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
            {membershipPlan.features.map((feature) => <li className="flex items-start gap-2" key={feature}><span className="grid size-5 shrink-0 place-items-center bg-primary/10"><Check className="size-3 text-primary" /></span>{feature}</li>)}
          </ul>
        </motion.article>

        <section className="mt-14 grid gap-8 border-t border-border pt-8 sm:grid-cols-2 lg:mt-20 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left">
            <ShieldCheck className="mt-1 size-6 shrink-0 text-primary" />
            <div>
              <h2 className="font-display text-2xl font-black uppercase">Tu hésites encore ?</h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground">Viens tester un cours gratuitement. L’équipe t’aidera à trouver le rythme et la discipline qui te correspondent.</p>
            </div>
          </div>
          <Button asChild className="mx-auto sm:mx-0" size="xl"><Link href="/inscription">Commencer mon inscription <ArrowRight /></Link></Button>
        </section>
      </div>
    </main>
  );
}
